<template>
  <div class="space-y-10 divide-y divide-gray-900/10">
    <div class="grid grid-cols-1 gap-x-8 gap-y-8 pt-10 md:grid-cols-3">
      <!-- Form intro -->
      <div class="px-4 sm:px-0">
        <h2 class="text-base font-semibold leading-7 text-gray-900">Sync Question Sets</h2>
        <p class="mt-1 text-sm leading-6 text-gray-600">
          Choose live and draft question sets to synchronize.
        </p>
      </div>

      <!-- Hint + Form -->
      <div class="md:col-span-2 space-y-4">
        <!-- Hint -->
        <div class="flex justify-end px-4">
          <button
              @click="showHint = !showHint"
              type="button"
              class="text-sm text-indigo-600 hover:underline focus:outline-none"
          >
            ℹ️ How it works
          </button>
        </div>

        <!-- Hint Content -->
        <transition name="fade">
          <div
              v-if="showHint"
              class="px-4 py-3 bg-blue-50 text-sm text-blue-900 rounded border border-blue-200 mx-4"
          >
            This tool compares the selected <strong>draft</strong> and <strong>live</strong> question sets.
            It identifies any changes (added, updated, removed questions, <strong>including changes in question order</strong>),
            and synchronises them. <br />
            If <strong>dry-run</strong> is enabled, it only shows what would change, without saving.<br />
            If <strong>dry-run</strong> is disabled, then question changes are applied.
          </div>
        </transition>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
        <div class="px-4 py-6 sm:p-8">
          <div class="grid grid-cols-1 sm:grid-cols-6 gap-x-6 gap-y-6">

            <!-- Draft Set -->
            <div class="sm:col-span-5">
              <label class="block text-sm font-medium text-gray-900">Draft Question Set</label>
              <v-select
                  v-model="form.draftSetId"
                  :options="questionSetsList"
                  label="name"
                  :reduce="item => item.id"
                  :clearable="false"
                  placeholder="Select draft set"
              />
            </div>

            <!-- Live Set -->
            <div class="sm:col-span-5">
              <label class="block text-sm font-medium text-gray-900">Live Question Set</label>
              <v-select
                  v-model="form.liveSetId"
                  :options="questionSetsList"
                  label="name"
                  :reduce="item => item.id"
                  :clearable="false"
                  placeholder="Select live set"
              />
            </div>

            <!-- Dry Run -->
            <div class="sm:col-span-5 flex items-center">
              <input
                  id="dry-run"
                  type="checkbox"
                  v-model="form.dryRun"
                  class="h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
              <label for="dry-run" class="ml-2 block text-sm text-gray-900">Dry-run mode</label>
            </div>

            <!-- Submit -->
            <div class="sm:col-span-5">
              <button
                  type="submit"
                  :disabled="loading"
                  class="mt-4 inline-flex items-center rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
              >
                <span v-if="loading">Running...</span>
                <span v-else>Run Sync</span>
              </button>
            </div>

            <!-- Error -->
            <div v-if="error" class="sm:col-span-5 text-sm text-red-600 mt-2">
              {{ error }}
            </div>
          </div>
        </div>
      </form>
      </div>
    </div>

    <!-- Output Section (below the form, full width) -->
    <div v-if="result" class="pt-6">
      <label class="block text-sm font-medium text-gray-900 mb-2">Result Output</label>
      <pre class="p-4 bg-gray-100 rounded text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto" v-text="formattedResult" />
    </div>
  </div>
</template>


<script>
import axios from 'axios'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  components: {
    'v-select': vSelect,
  },
  props: {
    draftQuestionSetId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      form: {
        liveSetId: null,
        draftSetId: this.draftQuestionSetId,
        dryRun: true,
      },
      loading: false,
      error: null,
      result: null,
      questionSetsList: [{key: null, name: '---'}],
      showHint: false,
    }
  },
  computed: {
    formattedResult() {
      if (!this.result) return ''
      const lines = [
        `Dry-run: ${this.result.dryRun}`,
        `Changes count: ${this.result.without_order_changes_count}`,
        `Order changes count: ${this.result.order_changes_count}`,
        '',
        'Changes:',
        ...this.result.changes.map(c => `- ${c.action}: ${c.question_text} (ID: ${c.question_id})`)
      ]
      return lines.join('\n')
    }
  },
  methods: {
    async handleSubmit() {
      this.loading = true
      this.result = null
      this.error = null

      try {
        const response = await axios.post('/member-firm/question-set/question-sets/publish', this.form)
        this.result = response.data
      } catch (err) {
        if (err.response?.data?.errors) {
          const firstError = Object.values(err.response.data.errors)[0]
          this.error = Array.isArray(firstError) ? firstError[0] : firstError
        } else {
          this.error = 'An unexpected error occurred.'
        }
      } finally {
        this.loading = false
      }
    },
    getQuestionSetsList() {
      axios.get(`/member-firm/question-section/form-data/question-sets-list`).then((res) => {
        this.questionSetsList = res.data.data;
      }).catch((error) => {
        console.log('error', error)
      })
    },
  },
  created() {
    this.getQuestionSetsList();
  }
}
</script>

<style scoped>
.v-select {
  width: 100%;
}
</style>
